import React, { useState, useEffect } from 'react';
import { useWishlist } from '../../context/WishlistContext';
import { useToast } from '../../context/ToastContext';
import { useUserAuth } from '../../context/UserAuthContext';
import ProductCard from '../common/ProductCard';
import './WishlistPage.css';
import { Link } from 'react-router-dom';
import { ShoppingBag, Loader } from 'lucide-react';
import * as WishlistService from '../../services/WishlistService';

const WishlistPage = () => {
  const { wishlist, loading: contextLoading, removeFromWishlist: removeFromContext } = useWishlist();
  const { showInfoToast, showSuccessToast, showErrorToast } = useToast();
  const { currentUser, isAuthenticated } = useUserAuth();
  const [dbWishlistItems, setDbWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch wishlist items from database if user is authenticated
  useEffect(() => {
    const fetchWishlistItems = async () => {
      if (isAuthenticated) {
        try {
          setLoading(true);
          const items = await WishlistService.getWishlistItems();
          console.log('Fetched wishlist items:', items);
          setDbWishlistItems(items);
        } catch (error) {
          console.error('Error fetching wishlist:', error);
          showErrorToast('Failed to load your wishlist. Please try again.');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(contextLoading);
      }
    };

    fetchWishlistItems();
  }, [isAuthenticated, contextLoading, showErrorToast]);

  // Show empty wishlist message
  useEffect(() => {
    const items = isAuthenticated ? dbWishlistItems : wishlist;
    if (!loading && items.length === 0) {
      showInfoToast('Your wishlist is empty. Browse our collections to add items!');
    }
  }, [loading, wishlist.length, dbWishlistItems.length, isAuthenticated, showInfoToast]);

  // Remove item from wishlist
  const handleRemoveItem = async (productId) => {
    if (isAuthenticated) {
      try {
        const success = await WishlistService.removeFromWishlist(productId);
        if (success) {
          setDbWishlistItems(dbWishlistItems.filter(item => 
            (item.product_id !== productId && item.id !== productId)
          ));
          showSuccessToast('Item removed from wishlist');
        }
      } catch (error) {
        console.error('Error removing from wishlist:', error);
        showErrorToast('Failed to remove item from wishlist');
      }
    } else {
      removeFromContext(productId);
    }
  };

  if (loading) {
    return (
      <section className="wishlist-page">
        <div className="container">
          <div className="loading-container">
            <Loader size={40} className="loading-spinner" />
            <p>Loading your wishlist...</p>
          </div>
        </div>
      </section>
    );
  }

  // If not authenticated, show login prompt
  if (!isAuthenticated && !contextLoading) {
    return (
      <section className="wishlist-page" aria-labelledby="wishlist-title">
        <div className="container">
          <h2 className="section-title" id="wishlist-title">My Wishlist</h2>
          <div className="empty-wishlist" aria-live="polite">
            <div className="empty-wishlist-content">
              <ShoppingBag size={48} className="empty-icon" />
              <h3>Please log in to view your wishlist</h3>
              <p>Log in to save your wishlist items permanently and access them from any device</p>
              <Link to="/auth" className="browse-button">
                Log In / Register
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Determine which wishlist to use
  const items = isAuthenticated ? dbWishlistItems : wishlist;

  // Process items to ensure they have all required fields
  const processedItems = items.filter(item => {
    // Only filter out completely invalid items, but keep "Product direct" items
    if (!item || (!item.name && !item.product_id && !item.id)) {
      console.warn('Filtering out invalid product:', item);
      return false;
    }
    return true;
  }).map(item => {
    // Ensure image path is correct
    let imagePath = item.image;
    if (imagePath) {
      if (!imagePath.startsWith('http') && !imagePath.startsWith('/')) {
        imagePath = '/' + imagePath;
      }
    } else {
      imagePath = '/media/placeholder.jpeg';
    }

    return {
      ...item,
      id: item.product_id || item.id,
      name: item.name || 'Product Name',
      price: item.price || 'Request for price',
      image: imagePath,
      category: item.category || 'uncategorized'
    };
  });

  return (
    <section className="wishlist-page" aria-labelledby="wishlist-title">
      <div className="container">
        <h2 className="section-title" id="wishlist-title">My Wishlist</h2>
        <p className="section-subtitle">Your favorite futuristic jewelry pieces</p>

        {processedItems.length > 0 ? (
          <div className="wishlist-content">
            <p className="wishlist-count-info" aria-live="polite">
              You have {processedItems.length} {processedItems.length === 1 ? 'item' : 'items'} in your wishlist
            </p>
            <div className="product-grid" role="list">
              {processedItems.map((item) => (
                <div key={item.id || item.product_id} role="listitem" className="product-grid-item">
                  <ProductCard 
                    product={item} 
                    onRemoveFromWishlist={() => handleRemoveItem(item.id || item.product_id)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="empty-wishlist" aria-live="polite">
            <div className="empty-wishlist-content">
              <ShoppingBag size={48} className="empty-icon" />
              <h3>Your wishlist is empty</h3>
              <p>Explore our collections and add your favorite pieces to your wishlist</p>
              <Link to="/collections" className="browse-button">
                Browse Collections
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WishlistPage; 